/** HistogramBars — engagement-rate distribution with creator's bin highlighted. */
import { useState } from 'react';

export interface HistogramBin {
  min: number | null;
  max: number | null;
  totalProfileCount: number;
  isMedian: boolean;
}

interface HistogramBarsProps {
  bins: HistogramBin[];
  creatorValue: number | null;
}

// Figma plot coordinate system (viewBox units). The SVG scales to container
// width via `w-full`, preserving these proportions.
const W = 905;
const H = 272;
const X_LEFT = 38; // bars/gridlines start (leaves a gutter for the y labels)
const X_RIGHT = 867; // bars/gridlines end
const Y_TOP = 22; // top gridline / top of the tallest bar
const Y_BASE = 244; // baseline
const PLOT_W = X_RIGHT - X_LEFT; // 829
const PLOT_H = Y_BASE - Y_TOP; // 222
const GRID_LINES = 5; // top, baseline, and 3 between
const BAR_RADIUS = 4;
const BAR_FILL_RATIO = 0.81; // Figma bar width ÷ pitch (25.885 / 31.88)

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(Math.round(n));
}

/** Engagement-rate fraction → percent string, fixed 2dp. Axis/band boundary
 *  labels (not a measured stat), so 0 is a legitimate value here. */
export function fmtErPct(fraction: number): string {
  return `${(fraction * 100).toFixed(2)}%`;
}

function bandLabel(b: HistogramBin): string {
  if (b.min == null) return `< ${fmtErPct(b.max ?? 0)}`;
  if (b.max == null) return `${fmtErPct(b.min)}+`;
  return `${fmtErPct(b.min)}–${fmtErPct(b.max)}`;
}

/** Path for a rectangle with only its top-left/top-right corners rounded. */
function topRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, h, w / 2));
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
}

export function HistogramBars({ bins, creatorValue }: HistogramBarsProps) {
  const [active, setActive] = useState<number | null>(null);

  if (bins.length === 0) return null;

  const maxCount = Math.max(...bins.map((b) => b.totalProfileCount), 1);
  const pitch = PLOT_W / bins.length;
  const barWidth = pitch * BAR_FILL_RATIO;

  // Engagement-rate range spanned by the bins (for x-axis ticks).
  const xMin = bins[0]?.min ?? 0;
  const lastBin = bins[bins.length - 1];
  const xMax = lastBin?.max ?? lastBin?.min ?? xMin;

  // Find creator's bin (first bin where creator's value falls in [min, max)).
  let creatorBinIdx = -1;
  if (creatorValue != null) {
    creatorBinIdx = bins.findIndex((b) => {
      const lo = b.min ?? -Infinity;
      const hi = b.max ?? Infinity;
      return creatorValue >= lo && creatorValue < hi;
    });
    if (creatorBinIdx === -1 && lastBin && creatorValue >= (lastBin.min ?? -Infinity)) {
      creatorBinIdx = bins.length - 1;
    }
  }

  const medianIdx = bins.findIndex((b) => b.isMedian);

  const activeBin = active != null ? bins[active] : null;
  const tipLeftPct =
    active != null
      ? Math.min(94, Math.max(6, ((X_LEFT + active * pitch + pitch / 2) / W) * 100))
      : 0;

  const midEr = (xMin + xMax) / 2;

  return (
    <div className="relative w-full">
      {activeBin && (
        <div
          className="pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-stone-900 px-2 py-1 text-[10px] font-medium leading-tight text-white"
          style={{ left: `${tipLeftPct}%` }}
        >
          <div>ER {bandLabel(activeBin)}</div>
          <div className="font-medium text-stone-300">
            {activeBin.totalProfileCount.toLocaleString('en-US')} profiles
          </div>
          {active === creatorBinIdx && (
            <div className="text-(--brand-600)">← this creator</div>
          )}
          {active === medianIdx && <div className="text-stone-400">median band</div>}
        </div>
      )}

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={`Engagement rate distribution across ${bins
          .reduce((s, b) => s + b.totalProfileCount, 0)
          .toLocaleString('en-US')} profiles, ranging ${fmtErPct(xMin)} to ${fmtErPct(xMax)}.`}
      >
        {/* Horizontal gridlines (dashed) — max at top, 0 at bottom */}
        {Array.from({ length: GRID_LINES }, (_, i) => {
          const y = Y_TOP + (i * PLOT_H) / (GRID_LINES - 1);
          return (
            <line
              key={`grid-${i}`}
              x1={X_LEFT}
              y1={y}
              x2={X_RIGHT}
              y2={y}
              stroke="var(--border-default)"
              strokeWidth={1}
              strokeDasharray="4,4"
            />
          );
        })}

        {/* Y labels: max count (top) and 0 (bottom) */}
        <text
          x={X_LEFT - 8}
          y={Y_TOP}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={500}
          fill="var(--foreground-muted)"
        >
          {fmtCount(maxCount)}
        </text>
        <text
          x={X_LEFT - 8}
          y={Y_BASE}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={500}
          fill="var(--foreground-muted)"
        >
          0
        </text>

        {/* Bars */}
        {bins.map((b, i) => {
          const barH = (b.totalProfileCount / maxCount) * PLOT_H;
          const x = X_LEFT + i * pitch + (pitch - barWidth) / 2;
          const y = Y_BASE - barH;
          const fill =
            i === creatorBinIdx
              ? 'var(--brand-600)'
              : i === active
                ? 'var(--stone-300)'
                : 'var(--stone-200)';
          return <path key={i} d={topRoundedRect(x, y, barWidth, barH, BAR_RADIUS)} fill={fill} />;
        })}

        {/* Median guide line (gray dashed) + inline label */}
        {medianIdx >= 0 &&
          (() => {
            const medianX = X_LEFT + medianIdx * pitch + pitch / 2;
            const nearRightEdge = medianX > W - 80;
            return (
              <>
                <line
                  x1={medianX}
                  y1={Y_TOP - 6}
                  x2={medianX}
                  y2={Y_BASE}
                  stroke="var(--foreground-subtle)"
                  strokeWidth={1.5}
                  strokeDasharray="4,4"
                />
                <text
                  x={nearRightEdge ? medianX - 6 : medianX + 6}
                  y={Y_TOP - 8}
                  textAnchor={nearRightEdge ? 'end' : 'start'}
                  dominantBaseline="hanging"
                  fontSize={12}
                  fontWeight={500}
                  fill="var(--foreground-default)"
                >
                  Median
                </text>
              </>
            );
          })()}

        {/* X ticks: range start / midpoint / end (engagement rate %) */}
        <text
          x={X_LEFT}
          y={Y_BASE + 22}
          textAnchor="start"
          fontSize={12}
          fontWeight={500}
          fill="var(--foreground-muted)"
        >
          {fmtErPct(xMin)}
        </text>
        <text
          x={X_LEFT + PLOT_W / 2}
          y={Y_BASE + 22}
          textAnchor="middle"
          fontSize={12}
          fontWeight={500}
          fill="var(--foreground-muted)"
        >
          {fmtErPct(midEr)}
        </text>
        <text
          x={X_RIGHT}
          y={Y_BASE + 22}
          textAnchor="end"
          fontSize={12}
          fontWeight={500}
          fill="var(--foreground-muted)"
        >
          {fmtErPct(xMax)}
        </text>

        {/* Transparent hit areas (full plot height) for hover/tap readout */}
        {bins.map((b, i) => (
          <rect
            key={`hit-${i}`}
            x={X_LEFT + i * pitch}
            y={Y_TOP - 6}
            width={pitch}
            height={PLOT_H + 6}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onPointerEnter={() => setActive(i)}
            onPointerDown={() => setActive(i)}
            onPointerLeave={(e) => {
              if (e.pointerType === 'mouse') setActive((cur) => (cur === i ? null : cur));
            }}
            aria-hidden
          />
        ))}
      </svg>
    </div>
  );
}
